import { createAgGridColumnConfig } from '@opengov/reporting-types';
import { reportConfigurationActionCreators } from '../../../../module/state';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects/reportConfigurationEffects';
import { IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { assertExhaustive } from '../../../../src/utils/assertExhaustive';
import {
  BooleanOperators,
  FilterOperators,
  IColumnFilter,
  IColumnFilterBooleanOperator
} from '@opengov/reporting-types/src/reportFilters';
import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';
import { FilterCellRenderer } from '../FilterCellRenderer/FilterCellRenderer';
import { DetailTableTextFilter, DetailTableTextFilterCondition } from '../DetailTableTextFilter';
import { DetailTableNumericRangeFilter } from '../DetailTableNumericRangeFilter';
import { DetailTableDateRangeFilter } from '../DetailTableDateRangeFilter';
import { ColumnState } from 'ag-grid-community';
import { OrderedMap } from 'immutable';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export function updateColumnConfig(newColumnState: ColumnState[]) {
  // getColumnState() returns columns in display order, so the array index is the column's
  // position. Do NOT use ColumnState.sortIndex here: that is the column's rank within the
  // active grid sort, not its display position, so a sort on a non-first column would persist
  // a colliding position and make columns jump on the next render. See BUD-17949.
  const config = newColumnState.map((column, index) =>
    createAgGridColumnConfig({
      columnName: column.colId || '',
      pinned: (column.pinned as string | boolean | undefined) ?? undefined,
      hide: column.hide || false,
      sortedIndex: index
    })
  );
  reportConfigurationActionCreators.setAgGridColumnConfig(config);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
}

export function formatFilterDate(filterDate: string) {
  return dayjs(filterDate).format('MM/DD/YYYY');
}

// The numeric and date filters describe a single interval — a lower bound and an upper bound that
// have to hold together — so their conditions are always joined with AND. Deriving this from the
// saved boolean operators instead produced 'OR' for every column with no stored entry, which is
// how legacy range filters were saved; ag-grid then wrote that 'OR' back to the report
// configuration, making the range match every row (BUD-18751).
const RANGE_FILTER_JOIN_OPERATOR = 'AND' as const;

export type AgGridColumnFilterModel = {
  filterType?: string;
  values?: string[] | number[];
  type?: string;
  filter?: number | string | null;
  filterTo?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  operator?: 'AND' | 'OR';
  conditions?: AgGridColumnFilterModel[];
  filterModels?: (AgGridColumnFilterModel | null)[];
  filters?: DetailTableTextFilterCondition[];
  booleanOperator?: BooleanOperators;
};

/**
 * Builds the initial filterModel for AG Grid initialState from report configuration filters.
 * Maps IColumnFilter[] and columns to the shape expected by gridOptions.initialState.filter.filterModel.
 */

export const convertFiltersToFilterModel = ({
  columns,
  filters,
  filterBooleanOperators = []
}: {
  columns: IReportColumn[];
  filters: IColumnFilter[] | null;
  filterBooleanOperators?: IColumnFilterBooleanOperator[];
}): Record<string, AgGridColumnFilterModel> => {
  if (!filters || filters.length === 0) return {};

  const columnByName = new Map(columns.map((col) => [col.name, col]));
  const model: Record<string, AgGridColumnFilterModel> = {};
  const columnNamesWithFilters = [...new Set(filters.map((f) => f.columnName).filter(Boolean))] as string[];

  for (const columnName of columnNamesWithFilters) {
    const column = columnByName.get(columnName);
    if (!column) continue;

    const columnType = getTableColumnTypeFromReportColumnType(column);
    const columnFilters = filters.filter((f) => f.columnName === columnName);

    switch (columnType) {
      case ColumnTypes.Boolean:
        {
          const equalFilters = columnFilters.filter((f) => f.operator === FilterOperators.Equal && f.value !== null);
          if (equalFilters.length > 0) {
            model[columnName] = getTextColumnFilterModel(equalFilters.map((f) => f.value as string));
          }
        }
        break;
      case ColumnTypes.Text:
        {
          const eligibleFilters = columnFilters.filter((f) => {
            const isBlank = f.operator === FilterOperators.Blank || f.operator === FilterOperators.NotBlank;
            return isBlank || f.value !== null;
          });
          const detailFilters: DetailTableTextFilterCondition[] = eligibleFilters.map(({ operator, value }) => {
            const isBlank = operator === FilterOperators.Blank || operator === FilterOperators.NotBlank;
            return {
              operator: operator as FilterOperators,
              text: isBlank ? '' : ((value as string | null | undefined) ?? '')
            };
          });
          const boValue =
            filterBooleanOperators.find((bo) => bo.columnName === columnName)?.booleanOperator ?? BooleanOperators.And;
          if (detailFilters.length > 0) {
            model[columnName] = {
              filterType: 'detailTableText',
              filters: detailFilters,
              booleanOperator: boValue
            };
          }
        }
        break;
      case ColumnTypes.Number:
      case ColumnTypes.Currency:
      case ColumnTypes.Percent:
      case ColumnTypes.Period:
      case ColumnTypes.Year:
        {
          const filter = columnFilters.find(
            (f) => f.operator === FilterOperators.Greater || f.operator === FilterOperators.Equal
          );
          const filterTo = columnFilters.find(
            (f) => f.operator === FilterOperators.Lesser || f.operator === FilterOperators.Equal
          );
          const filterValue = filter?.value != null ? Number(filter.value) : null;
          const filterToValue = filterTo?.value != null ? Number(filterTo.value) : null;
          model[columnName] = getNumberColumnFilterModel(
            Number.isFinite(filterValue) ? filterValue : null,
            Number.isFinite(filterToValue) ? filterToValue : null,
            RANGE_FILTER_JOIN_OPERATOR
          );
        }
        break;
      case ColumnTypes.Date:
        {
          const dateFromFilter = columnFilters.find(
            (f) => f.operator === FilterOperators.Greater || f.operator === FilterOperators.Equal
          );
          const dateToFilter = columnFilters.find(
            (f) => f.operator === FilterOperators.Lesser || f.operator === FilterOperators.Equal
          );
          model[columnName] = getDateColumnFilterModel(
            dateFromFilter?.value as string | null,
            dateToFilter?.value as string | null,
            RANGE_FILTER_JOIN_OPERATOR
          );
        }
        break;
      default:
        assertExhaustive(columnType);
    }
  }

  return model;
};

export function getTextColumnFilterModel(values: string[]): AgGridColumnFilterModel {
  return { filterType: 'set', values };
}

export function getDateColumnFilterModel(
  dateFrom: string | null,
  dateTo: string | null,
  operator: 'AND' | 'OR'
): AgGridColumnFilterModel {
  let conditions: AgGridColumnFilterModel[] = [];
  if (dateFrom && dateTo && dateFrom === dateTo) {
    // Equal bounds are an exact match — represent as a single `equal` condition so
    // it round-trips into the date filter's Equals input and shows as one chip.
    conditions = [{ filterType: 'date', type: FilterOperators.Equal, dateFrom }];
  } else {
    if (dateFrom) {
      conditions = [...conditions, { filterType: 'date', type: FilterOperators.Greater, dateFrom }];
    }
    if (dateTo) {
      conditions = [...conditions, { filterType: 'date', type: FilterOperators.Lesser, dateFrom: dateTo }];
    }
  }
  return { filterType: 'date', operator, conditions };
}

export function getNumberColumnFilterModel(
  filter: number | null,
  filterTo: number | null,
  operator: 'AND' | 'OR'
): AgGridColumnFilterModel {
  const hasFilter = filter !== null && typeof filter === 'number';
  const hasFilterTo = filterTo !== null && typeof filterTo === 'number';
  let conditions: AgGridColumnFilterModel[] = [];
  if (hasFilter && hasFilterTo && filter === filterTo) {
    // Equal bounds are an exact match — represent as a single `equal` condition so
    // it round-trips into the numeric filter's Equals input and shows as one chip.
    conditions = [{ filterType: 'number', type: FilterOperators.Equal, filter }];
  } else {
    if (hasFilter) {
      conditions = [...conditions, { filterType: 'number', type: FilterOperators.Greater, filter }];
    }
    if (hasFilterTo) {
      conditions = [...conditions, { filterType: 'number', type: FilterOperators.Lesser, filter: filterTo }];
    }
  }
  return { filterType: 'number', operator, conditions };
}

const TEXT_FILTER_PARAMS = {} as const;

// Stable module-level handler and filter definition for DetailTableTextFilter.
// With enableFilterHandlers:true, ag-grid's createHandler() uses DUMMY_HANDLER as a fallback,
// setting isHandler:true. But filterParamsChanged() uses createHandlerFunc() (no DUMMY_HANDLER),
// so a plain React component gives isHandler:false — mismatch triggers destroyFilter() on every
// colDefChanged event. Providing an explicit handler makes createHandlerFunc() return non-null
// consistently, and the stable reference prevents handlerGenerator mismatch on column def updates.
const DETAIL_TABLE_TEXT_HANDLER = () => ({ doesFilterPass: () => true });
const DETAIL_TABLE_TEXT_FILTER_DEF = {
  component: DetailTableTextFilter,
  handler: DETAIL_TABLE_TEXT_HANDLER
};

const DETAIL_TABLE_NUMERIC_RANGE_HANDLER = () => ({ doesFilterPass: () => true });
const DETAIL_TABLE_NUMERIC_RANGE_FILTER_DEF = {
  component: DetailTableNumericRangeFilter,
  handler: DETAIL_TABLE_NUMERIC_RANGE_HANDLER
};
const NUMERIC_RANGE_FROM_TO_PARAMS = {
  fromLabel: 'From',
  toLabel: 'To',
  equalsLabel: 'Equals',
  showEquals: true
} as const;
const NUMERIC_RANGE_GTE_LTE_PARAMS = {
  fromLabel: 'Greater than or equal to',
  toLabel: 'Less than or equal to'
} as const;

const DETAIL_TABLE_DATE_RANGE_HANDLER = () => ({ doesFilterPass: () => true });
const DETAIL_TABLE_DATE_RANGE_FILTER_DEF = {
  component: DetailTableDateRangeFilter,
  handler: DETAIL_TABLE_DATE_RANGE_HANDLER
};
const DATE_RANGE_FROM_TO_PARAMS = {
  fromLabel: 'From',
  toLabel: 'To',
  equalsLabel: 'Equals',
  showEquals: true
} as const;

export function getFilterParamsByColumnType(
  columnType: ColumnTypes,
  {
    filterValuesByColumn,
    column
  }: { filterValuesByColumn: OrderedMap<string, OrderedMap<string, number>>; column: IReportColumn }
) {
  switch (columnType) {
    case ColumnTypes.Boolean: {
      const valuesWithCounts: [string, number][] = Array.from(filterValuesByColumn.get(column.name) || []);
      const values = valuesWithCounts.map((value) => value[0]);
      // Use callback form so AG Grid knows we provide values (required for server-side row model).
      // See https://www.ag-grid.com/react-data-grid/errors/113/
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          values: (params: { success: (values: string[]) => void }) => {
            params.success(values);
          },
          refreshValuesOnOpen: true,
          cellRenderer: FilterCellRenderer,
          cellRendererParams: { valuesWithCounts }
        }
      };
    }
    case ColumnTypes.Text: {
      return {
        filter: DETAIL_TABLE_TEXT_FILTER_DEF,
        filterParams: TEXT_FILTER_PARAMS
      };
    }
    case ColumnTypes.Date:
      // ag-grid's built-in date filter exposes an operator dropdown whose options are
      // greater-than/less-than only, which forced customers to offset a range by a day on
      // each side. The custom panel mirrors the numeric one: inclusive From/To plus an
      // exact-match Equals, all of which the backend already supports (BUD-18569).
      return {
        filter: DETAIL_TABLE_DATE_RANGE_FILTER_DEF,
        filterParams: DATE_RANGE_FROM_TO_PARAMS
      };
    case ColumnTypes.Year:
    case ColumnTypes.Period:
      return {
        filter: DETAIL_TABLE_NUMERIC_RANGE_FILTER_DEF,
        filterParams: NUMERIC_RANGE_FROM_TO_PARAMS
      };
    case ColumnTypes.Percent:
    case ColumnTypes.Currency:
    case ColumnTypes.Number:
      return {
        filter: DETAIL_TABLE_NUMERIC_RANGE_FILTER_DEF,
        filterParams: NUMERIC_RANGE_GTE_LTE_PARAMS
      };
    default:
      return assertExhaustive(columnType);
  }
}
